import * as React from "react";
import { useEffect, useState } from "react";
import { ICSIDUserStatus } from "../../../store/csid/types";
import * as services from "../../../store/service";
import * as csidUtil from "../../csid/csid";
import { toggleFullScreenLoader } from "../../../util/common";
import FullSSNComponent from "../../csid/FullSSNComponent";
import Last4SSNDigitComponent from "../../csid/Last4SSNDigitComponent";
import SSNSystemErrorComponent from "../../csid/SSNSystemErrorComponent";
import L3CallInComponent from "../L3/L3CallInComponent";
import L3ContinueComponent from "../L3/L3ContinueComponent";
import L3DisclosureComponent from "../L3/L3DisclosureComponent";
import L3SecurityQuestionComponent from "../L3/L3SecurityQuestionComponent";
import L3SystemErrorComponent from "../L3/L3SystemErrorComponent";
import L3SuccessComponent from "../L3/L3SuccessComponent";
import { DialogComponent } from "../../common/DialogComponent";

import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../../store";
import * as CSIDActions from "../../../store/csid/actions";
import { useTranslation } from "react-i18next";

interface IProps {
    initCSIDUserStatus: ICSIDUserStatus;
    creditAuthCallback: any;
    displayFlowAsDialog?: boolean;
    showClosebtn?: boolean;
    onCloseCallback?: any;
    l3Case?: string;
    continueFlowWithL3?: boolean;
    componentParent?: string;
}

const EZSCreditAuthentication: React.FunctionComponent<IProps> = (props) => {
    const status = {
        success: "success",
        error: "error",
        apiStatus: "api-status",
        timeout: "timeout",
        attemptFail: "attempt-fail",
        systemError: "system-error",
        l3LastTryFail: "l3-last-try-fail",
        l3OtpLocked: "l3-otp-locked",
        locked: "locked",
        l3LastTryTimemout: "l3-timeout-locked",
        l3AttemptFail: "l3-attempt-fail",
        l3Timeout: "l3-timeout",
        thinClient: "thin-client",
        manualAuthentication: "manual-authentication",
     };

    const [showCallInComponent, setShowCallInComponent] = useState(false);
    const [showInitialL3Continue, setShowInitialL3Continue] = useState(false);
    const [showDisclosureDialog, setShowDisclosureDiaglog] = useState(false);
    const [disclosureAgreed, setDisclosureAgreed] = useState(false);
    const [showLast4SSNDialog, setShowLast4SSNDialog] = useState(false);
    const [showFullSSNDialog, setShowFullSSNDialog] = useState(false);
    const [showSSNSystemError, setShowSSNSystemError] = useState(false);
    const [showL3SecurityQuestionDialog, setShowL3SecurityQuestionDialog] = useState(false);
    const [showL3VerifiedDialog, setShowL3VerifiedDialog] = useState(false);
    const [showL3ContinueDialogForError, setShowL3ContinueDialogForError] = useState(false);
    const [showL3SystemError, setShowL3SystemError] = useState(false);

    const [csidUserStatus, setCSIDUserStatus] = useState(props.initCSIDUserStatus);
    const [callInReason, setCallInReason] = useState("manual-authentication");
    const [messageKeyForL3ContinueWithError, setMessageKeyForL3ContinueWithError] = useState("credit-service");
    const [ssn, setSSN] = useState("0");
    const [ssnSystemErrorMessage, setSSNSystemErrorMessage] = useState("");
    const [l3SecurityQuestionTryCount, setL3SecurityQuestionTryCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // default values for props
    let blnDisplayFlowAsDialog = false;
    if (props.displayFlowAsDialog != null) {
        blnDisplayFlowAsDialog = props.displayFlowAsDialog;
    }
    let showCloseBtn = false;
    if (props.showClosebtn != null) {
        showCloseBtn = props.showClosebtn;
    }

    let blnContinueFlowWithL3 = true;
    if (props.continueFlowWithL3 != null) {
        blnContinueFlowWithL3 = props.continueFlowWithL3;
    }
    //
    const globalTryCount = useSelector((state: ApplicationState) => {
        if (state.csidUserStatusState) {
            return state.csidUserStatusState?.globalQuestionTryCount;
        }
    });
    const dispatch = useDispatch();
    const { t: tLang } = useTranslation();
    const setGlobalTryCount = (tryCount: number) => dispatch(CSIDActions.actionCreators.setGlobalQuestionTryCount(tryCount));
    useEffect(() => {
        setL3SecurityQuestionTryCount(globalTryCount != null ? globalTryCount : 1);

        const nextStep: csidUtil.NEXT_STEP = csidUtil.getNextStep(props.initCSIDUserStatus);
        switch (nextStep) {
            case csidUtil.NEXT_STEP.SHOW_TO_CALL_IN:
                // show call in component
                setShowCallInComponent(true);
                break;
            default:
                setShowInitialL3Continue(true);
                // to be on safe side, default show call-in component
                break;
        }
    }, []);

    useEffect(() => {
        if (isLoading) {
            toggleFullScreenLoader(true);
        } else {
            toggleFullScreenLoader(false);
        }
    }, [isLoading]);

    const getCSIDUserStatusAndProceedToNext = () => {
        setIsLoading(true);
        services
            .getUserCSIDStatus()
            .then((data) => {
                setIsLoading(false);
                const csidUserStatus: ICSIDUserStatus = data as ICSIDUserStatus;
                setCSIDUserStatus(csidUserStatus);
                const nextStep: csidUtil.NEXT_STEP = csidUtil.getNextStep(csidUserStatus);
                switch (nextStep) {
                    case csidUtil.NEXT_STEP.CAPTURE_LAST_4_SSN:
                        setShowLast4SSNDialog(true);
                        break;
                    case csidUtil.NEXT_STEP.CAPTURE_FULL_SSN:
                        setShowFullSSNDialog(true);
                        break;
                    case csidUtil.NEXT_STEP.TRIGGER_L3_AUTHENTICATION:
                        setShowL3SecurityQuestionDialog(true);
                        break;
                    case csidUtil.NEXT_STEP.GO_TO_DASHBOARD:
                        // TODO:
                        if (props.creditAuthCallback != null) {
                            props.creditAuthCallback({ stauts: status.success, data: { detail: "go-to-dashboard-as-next-step" } });
                        }
                        break;
                    case csidUtil.NEXT_STEP.SHOW_TO_CALL_IN:
                        setShowCallInComponent(true);
                        break;
                    default:
                        // not expecting any steps other than case handled
                        // be on safe side
                        // display system-error
                        setShowL3SystemError(true);
                        break;
                }
            })
            .catch((error) => {
                setIsLoading(false);
                // if we're facing any issue while getting the status of the user from API
                // dispaly system-error
                setShowL3SystemError(true);
            });
    };

    // #region initial-continue
    const initialContinueSubmitCallBack = (callbackObj: any) => {
        setShowInitialL3Continue(false);
        const nextStep: csidUtil.NEXT_STEP = csidUtil.getNextStep(props.initCSIDUserStatus);
        switch (nextStep) {
            case csidUtil.NEXT_STEP.CAPTURE_LAST_4_SSN:
            case csidUtil.NEXT_STEP.CAPTURE_FULL_SSN:
            case csidUtil.NEXT_STEP.TRIGGER_L3_AUTHENTICATION:
                if (!disclosureAgreed) {
                    setShowDisclosureDiaglog(true);
                } else {
                    disclosureSubmitCallBack({ status: "success", data: "Agreed by user" });
                }
                break;
            default:
                break;
        }
    };
    // #endregion

    // #region disclosure
    const closeDisclosureDialogCallBack = (callbackObj: any) => {
        setShowDisclosureDiaglog(false);
        // if user close the disclosure dialog without agree, then in UI load "Verify my identity" component
        setShowInitialL3Continue(true);
        // close verify identity popup
        if (props.onCloseCallback != null) {
            props.onCloseCallback();
        }
    };

    const disclosureSubmitCallBack = (callbackObj: any) => {
        // close the disclosure dialog.
        setShowDisclosureDiaglog(false);
        // set the agree flag true for future use.
        setDisclosureAgreed(true);
        const nextStep: csidUtil.NEXT_STEP = csidUtil.getNextStep(props.initCSIDUserStatus);
        switch (nextStep) {
            case csidUtil.NEXT_STEP.CAPTURE_LAST_4_SSN:
                setShowLast4SSNDialog(true);
                break;
            case csidUtil.NEXT_STEP.CAPTURE_FULL_SSN:
                setShowFullSSNDialog(true);
                break;
            case csidUtil.NEXT_STEP.TRIGGER_L3_AUTHENTICATION:
                setShowL3SecurityQuestionDialog(true);
                break;
            default:
                break;
        }
    };

    // #endregion

    // #region last-4-SSN-dialog
    const closeLast4SSNDigitDialogComponent = (callbackObj: any) => {
        setShowLast4SSNDialog(false);
        // if user close the Last 4 SSN with cancel / ESC / X, then in UI load "Verify my identity" component
        setShowInitialL3Continue(true);
        // close verify identity popup
        if (props.onCloseCallback != null) {
            props.onCloseCallback();
        }
    };

    const submitLast4SSNDigitDialogComponent = (callbackObj: any) => {
        setShowLast4SSNDialog(false);
        if (callbackObj.status === status.success) {
            // api request successful
            // get the latest status from another api call and proceed to next step.
            if (blnContinueFlowWithL3) {
                setSSN(callbackObj.data.ssn);
                getCSIDUserStatusAndProceedToNext();
            } else {
                l3SuccessComponentContinueCallBack(null);
            }
        } else if (callbackObj.status === status.error) {
            if (callbackObj.data.reason === status.apiStatus) {
                getCSIDUserStatusAndProceedToNext();
            } else {
                // for api-error or api-exception
                // display call-in
                setSSNSystemErrorMessage(callbackObj.data.detail.message);
                setShowSSNSystemError(true);
            }
        }
    };
    // #endregion

    // #region fullSSN-dialog
    const closeFullSSNDialogComponent = (callbackObj: any) => {
        setShowFullSSNDialog(false);
        // if user close the Full SSN with cancel / ESC / X, then in UI load "Verify my identity" component
        setShowInitialL3Continue(true);
        // close verify identity popup
        if (props.onCloseCallback != null) {
            props.onCloseCallback();
        }
    };

    const submitFullSSNDialogComponent = (callbackObj: any) => {
        setShowFullSSNDialog(false);
        if (callbackObj.status === status.success) {
            // api request successful
            // get the latest status from another api call and proceed to next step.
            if (blnContinueFlowWithL3) {
                setSSN(callbackObj.data.ssn);
                getCSIDUserStatusAndProceedToNext();
            } else {
                l3SuccessComponentContinueCallBack(null);
            }
        } else if (callbackObj.status === status.error) {
            if (callbackObj.data.reason === status.apiStatus) {
                setCallInReason("full-ssn-failed");
                setShowCallInComponent(true);
            } else {
                // for api-error or api-exception
                // display call-in
                setSSNSystemErrorMessage(callbackObj.data.detail.message);
                setShowSSNSystemError(true);
            }
        }
    };
    // #endregion

    // #region SSN-system-error
    const ssnSystemErrorRefreshCallBack = (callbackObj: any) => {
        setShowSSNSystemError(false);
        getCSIDUserStatusAndProceedToNext();
    };

    const ssnSystemErrorCloseCallBack = (callbackObj: any) => {
        setShowSSNSystemError(false);
        setShowInitialL3Continue(true);
    };
    // #endregion

    // #region l3-security-question
    const closeL3SecurityQuestionDialogComponent = (callbackObj: any) => {
        setShowL3SecurityQuestionDialog(false);

        setL3SecurityQuestionTryCount(globalTryCount != null ? globalTryCount : 1);

        // if user close the L3 Security Question Dialog with cancel / ESC / X, then in UI load "Verify my identity" component
        //setGlobalTryCount(tempTryCount);
        if (props.componentParent === "dashboard") {
            if (props.onCloseCallback != null) {
                props.onCloseCallback();
            }
        } else {
            setShowInitialL3Continue(true);
        }
    };

    const submitL3SecurityQuestionDialogComponent = (callbackObj: any) => {
        setShowL3SecurityQuestionDialog(false);
        if (callbackObj.status === status.success) {
            // l3 authentication success
            setIsLoading(true);
            setShowL3VerifiedDialog(true);
            services
                .updateL3AttemptReason("SUCCESS", "L3 successful")
                .then((data) => {
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                });
            // api request successful
            // get the latest status from another api call and proceed to next step.
            // getCSIDUserStatusAndProceedToNext();
        } else if (callbackObj.status === status.error) {
            let errorReason = callbackObj.data.reason;
            if (errorReason === status.l3AttemptFail) {
                setMessageKeyForL3ContinueWithError(errorReason);
                setShowL3ContinueDialogForError(true);
            } else if (errorReason === status.l3LastTryFail) {
                setCallInReason(errorReason);
                setShowCallInComponent(true);
            } else if (errorReason === status.l3OtpLocked) {
                setCallInReason(errorReason);
                setShowCallInComponent(true);
            } else if (errorReason === status.locked) {
                setCallInReason(errorReason);
                setShowCallInComponent(true);
            } else {
                setShowL3SystemError(true);
            }
        }
    };

    const getL3QuestionErrorCallBack = (callbackObj: any) => {
        // close the dialog and show manual authentication to be on safe side
        setShowL3SecurityQuestionDialog(false);
        let errorReason = callbackObj.data.reason;
        if (errorReason === status.systemError) {
            setShowL3SystemError(true);
        } else {
            setCallInReason(errorReason);
            setShowCallInComponent(true);
        }
    };

    const l3SecurityQuestionDialogTimeOut = (callbackObj: any) => {
        setShowL3SecurityQuestionDialog(false);
        let errorReason = callbackObj.data.reason;
        if (errorReason === status.l3LastTryTimemout) {
            setCallInReason(errorReason);
            setShowCallInComponent(true);
        } else {
            setMessageKeyForL3ContinueWithError(errorReason);
            setShowL3ContinueDialogForError(true);
        }
    };

    const l3ContinueWithErrorSubmitCallBack = (callbackObj: any) => {
        setShowL3ContinueDialogForError(false);
        const tempTryCount = l3SecurityQuestionTryCount + 1;
        setL3SecurityQuestionTryCount(tempTryCount);
        setGlobalTryCount(tempTryCount);
        setShowL3SecurityQuestionDialog(true);
    };
    // #endregion

    // #region system-error
    const l3SystemErrorRefreshCallBack = (callbackObj: any) => {
        setShowL3SystemError(false);
        getCSIDUserStatusAndProceedToNext();
    };

    const l3SystemErrorCloseCallBack = (callbackObj: any) => {
        setShowL3SystemError(false);
        if (props.componentParent === "dashboard") {
            if (props.onCloseCallback != null) {
                props.onCloseCallback();
            }
        } else {
            setShowInitialL3Continue(true);
        }
    };
    // #endregion

    // #region l3-success
    const l3SuccessComponentContinueCallBack = (callbackObj: any) => {
        if (props.creditAuthCallback != null) {
            if (blnContinueFlowWithL3) {
                props.creditAuthCallback({ stauts: status.success, data: { detail: "l3-successful" } });
            } else {
                props.creditAuthCallback({ stauts: status.success, data: { detail: "ssn-successful" } });
            }
        }
    };
    // #endregion

    // #region call-in
    const callInContinueCallBack = () => {
        setShowCallInComponent(false);
        window.location.reload();
    };
    // #endregion

    return (
        <div>
            {
                // #region continue-flow-as-page
                !blnDisplayFlowAsDialog && (
                    <React.Fragment>
                        {showCallInComponent && (
                            <L3CallInComponent
                                closeButtonText={"Close"}
                                onCloseCallback={showCloseBtn != null ? props.onCloseCallback : null}
                                openAsDialog={false}
                                continueButtonText={tLang("dashboard.creditScore.l3CallInComp.finishVerification")}
                                onContinueButtonCallback={callInContinueCallBack}
                                callInReason={callInReason}
                                l3Case={props.l3Case}
                            ></L3CallInComponent>
                        )}

                        {showInitialL3Continue && (
                            <L3ContinueComponent
                                submitButtonText={"Try verification again"}
                                onSubmitCallback={initialContinueSubmitCallBack}
                                messageKey={"credit-service"}
                                openAsDialog={showCloseBtn}
                                closeButtonText={showCloseBtn != null ? "Close" : null}
                                onCloseCallback={props.onCloseCallback}
                                continueFlowWithL3={blnContinueFlowWithL3}
                            ></L3ContinueComponent>
                        )}

                        {showDisclosureDialog && (
                            <L3DisclosureComponent
                                closeButtonText={tLang("dashboard.creditScore.l3DisclosureComp.closeButton")}
                                onCloseCallback={closeDisclosureDialogCallBack}
                                openAsDialog={false}
                                submitButtonText={tLang("dashboard.creditScore.l3DisclosureComp.submitButton")}
                                onSubmitCallback={disclosureSubmitCallBack}
                                providerName={"experian"}
                            ></L3DisclosureComponent>
                        )}

                        {showLast4SSNDialog && (
                            <Last4SSNDigitComponent
                                closeButtonText={"Cancel"}
                                onCloseCallback={closeLast4SSNDigitDialogComponent}
                                openAsDialog={false}
                                submitButtonText={"Submit"}
                                onSubmitCallback={submitLast4SSNDigitDialogComponent}
                                displaySSNInputNumber={true}
                                csidUserStatus={csidUserStatus}
                            ></Last4SSNDigitComponent>
                        )}

                        {showFullSSNDialog && (
                            <FullSSNComponent
                                closeButtonText={"Cancel"}
                                onCloseCallback={closeFullSSNDialogComponent}
                                openAsDialog={false}
                                submitButtonText={"Submit"}
                                onSubmitCallback={submitFullSSNDialogComponent}
                                displaySSNInputNumber={false}
                                dobRequired={true}
                                csidUserStatus={csidUserStatus}
                                dispalyAdditionalHeaderMessage={true}
                            ></FullSSNComponent>
                        )}

                        {showSSNSystemError && (
                            <SSNSystemErrorComponent
                                errorMessage={ssnSystemErrorMessage}
                                refreshButtonText={"Refresh"}
                                onRefreshCallBack={ssnSystemErrorRefreshCallBack}
                                closeButtonText={null}
                                onCloseButtonCallBack={null}
                            ></SSNSystemErrorComponent>
                        )}

                        {showL3SecurityQuestionDialog && (
                            <L3SecurityQuestionComponent
                                closeButtonText={tLang("dashboard.creditScore.l3SecurityQuestionComp.cancelButtonText")}
                                onCloseCallback={closeL3SecurityQuestionDialogComponent}
                                openAsDialog={false}
                                submitButtonText={tLang("dashboard.creditScore.l3SecurityQuestionComp.submitButtonText")}
                                onSubmitCallback={submitL3SecurityQuestionDialogComponent}
                                errorCallback={getL3QuestionErrorCallBack}
                                ssn={ssn}
                                postponeActivation={false}
                                prepareForIDFL3={false}
                                displayTimerForTimeout={true}
                                timesInSecondBeforeTimeout={120}
                                onTimeoutCallback={l3SecurityQuestionDialogTimeOut}
                                currentTryCount={l3SecurityQuestionTryCount}
                                maxTryCount={2}
                            ></L3SecurityQuestionComponent>
                        )}

                        {showL3ContinueDialogForError && (
                            <L3ContinueComponent
                                submitButtonText={"Try verification again"}
                                onSubmitCallback={l3ContinueWithErrorSubmitCallBack}
                                messageKey={messageKeyForL3ContinueWithError}
                                continueFlowWithL3={blnContinueFlowWithL3}
                            ></L3ContinueComponent>
                        )}

                        {showL3SystemError && (
                            <L3SystemErrorComponent
                                refreshButtonText={tLang("dashboard.creditScore.l3SystemErrorComp.refreshButtonText")}
                                onRefreshCallBack={l3SystemErrorRefreshCallBack}
                                closeButtonText={null}
                                onCloseButtonCallBack={null}
                            ></L3SystemErrorComponent>
                        )}

                        {showL3VerifiedDialog && (
                            <L3SuccessComponent
                                openAsDialog={false}
                                continueButtonText={tLang("dashboard.creditScore.l3SuccessComp.continueButtonText")}
                                onContinueCallback={l3SuccessComponentContinueCallBack}
                            ></L3SuccessComponent>
                        )}
                    </React.Fragment>
                )
            }

            {
                // #region continue-flow-with-dialog
                blnDisplayFlowAsDialog && (
                    <React.Fragment>
                        {showCallInComponent && (
                            <L3CallInComponent
                                closeButtonText={""}
                                onCloseCallback={null}
                                openAsDialog={true}
                                continueButtonText={tLang("dashboard.creditScore.l3CallInComp.finishVerification")}
                                onContinueButtonCallback={callInContinueCallBack}
                                callInReason={callInReason}
                                l3Case={props.l3Case}
                            ></L3CallInComponent>
                        )}

                        {showInitialL3Continue && (
                            <L3ContinueComponent
                                submitButtonText={"Try verification again"}
                                onSubmitCallback={initialContinueSubmitCallBack}
                                messageKey={"credit-service"}
                                continueFlowWithL3={blnContinueFlowWithL3}
                            ></L3ContinueComponent>
                        )}

                        {showDisclosureDialog && (
                            <DialogComponent dialogId="dialog-l3-disclosure" title="L3 Disclosure" closeDialog={closeDisclosureDialogCallBack}  hideCloseButton={true}>
                                <L3DisclosureComponent
                                    closeButtonText={tLang("dashboard.creditScore.l3DisclosureComp.closeButton")}
                                    onCloseCallback={closeDisclosureDialogCallBack}
                                    openAsDialog={true}
                                    submitButtonText={tLang("dashboard.creditScore.l3DisclosureComp.submitButton")}
                                    onSubmitCallback={disclosureSubmitCallBack}
                                    providerName={"experian"}
                                ></L3DisclosureComponent>
                            </DialogComponent>
                        )}

                        {showLast4SSNDialog && (
                            <DialogComponent dialogId="dialog-ssn-pinning" title="SSN Pinning" closeDialog={closeLast4SSNDigitDialogComponent}>
                                <Last4SSNDigitComponent
                                    closeButtonText={"Cancel"}
                                    onCloseCallback={closeLast4SSNDigitDialogComponent}
                                    openAsDialog={true}
                                    submitButtonText={"Submit"}
                                    onSubmitCallback={submitLast4SSNDigitDialogComponent}
                                    displaySSNInputNumber={true}
                                    csidUserStatus={csidUserStatus}
                                ></Last4SSNDigitComponent>
                            </DialogComponent>
                        )}

                        {showFullSSNDialog && (
                            <DialogComponent dialogId="dialog-full-ssn" title="Full SSN" closeDialog={closeFullSSNDialogComponent}>
                                <FullSSNComponent
                                    closeButtonText={"Cancel"}
                                    onCloseCallback={closeFullSSNDialogComponent}
                                    openAsDialog={true}
                                    submitButtonText={"Submit"}
                                    onSubmitCallback={submitFullSSNDialogComponent}
                                    displaySSNInputNumber={false}
                                    dobRequired={true}
                                    csidUserStatus={csidUserStatus}
                                    dispalyAdditionalHeaderMessage={true}
                                ></FullSSNComponent>
                            </DialogComponent>
                        )}

                        {showSSNSystemError && (
                            <DialogComponent dialogId="dialog-ssn-system-error" title="SSN System Error" hideCloseButton={true}>
                                <SSNSystemErrorComponent
                                    errorMessage={ssnSystemErrorMessage}
                                    refreshButtonText={"Refresh"}
                                    onRefreshCallBack={ssnSystemErrorRefreshCallBack}
                                    closeButtonText={"Close"}
                                    onCloseButtonCallBack={ssnSystemErrorCloseCallBack}
                                ></SSNSystemErrorComponent>
                            </DialogComponent>
                        )}

                        {showL3SecurityQuestionDialog && (
                            <DialogComponent dialogId="dialog-l3-question" title="L3 Question" closeDialog={closeL3SecurityQuestionDialogComponent}  hideCloseButton={true}>
                                <L3SecurityQuestionComponent
                                    closeButtonText={tLang("dashboard.creditScore.l3SecurityQuestionComp.cancelButtonText")}
                                    onCloseCallback={closeL3SecurityQuestionDialogComponent}
                                    openAsDialog={false}
                                    submitButtonText={tLang("dashboard.creditScore.l3SecurityQuestionComp.submitButtonText")}
                                    onSubmitCallback={submitL3SecurityQuestionDialogComponent}
                                    errorCallback={getL3QuestionErrorCallBack}
                                    ssn={ssn}
                                    postponeActivation={false}
                                    prepareForIDFL3={false}
                                    displayTimerForTimeout={true}
                                    timesInSecondBeforeTimeout={120}
                                    onTimeoutCallback={l3SecurityQuestionDialogTimeOut}
                                    currentTryCount={l3SecurityQuestionTryCount}
                                    maxTryCount={2}
                                ></L3SecurityQuestionComponent>
                            </DialogComponent>
                        )}

                        {showL3ContinueDialogForError && (
                            <DialogComponent dialogId="dialog-l3-continue-with-error" title="L3 Continue with error" hideCloseButton={true}>
                                <L3ContinueComponent
                                    submitButtonText={"Try verification again"}
                                    onSubmitCallback={l3ContinueWithErrorSubmitCallBack}
                                    messageKey={messageKeyForL3ContinueWithError}
                                    continueFlowWithL3={blnContinueFlowWithL3}
                                ></L3ContinueComponent>
                            </DialogComponent>
                        )}

                        {showL3SystemError && (
                            <DialogComponent dialogId="dialog-l3-system-error" title="L3 System Error" hideCloseButton={true}>
                                <L3SystemErrorComponent
                                    refreshButtonText={tLang("dashboard.creditScore.l3SystemErrorComp.refreshButtonText")}
                                    onRefreshCallBack={l3SystemErrorRefreshCallBack}
                                    closeButtonText={tLang("dashboard.creditScore.l3SystemErrorComp.closeButtonText")}
                                    onCloseButtonCallBack={l3SystemErrorCloseCallBack}
                                ></L3SystemErrorComponent>
                            </DialogComponent>
                        )}

                        {showL3VerifiedDialog && (
                            <DialogComponent dialogId="dialog-l3-complete-with-success" title="L3 complete" hideCloseButton={true}>
                                <L3SuccessComponent
                                    openAsDialog={false}
                                    continueButtonText={tLang("dashboard.creditScore.l3SuccessComp.continueButtonText")}
                                    onContinueCallback={l3SuccessComponentContinueCallBack}
                                ></L3SuccessComponent>
                            </DialogComponent>
                        )}
                    </React.Fragment>
                )
            }
        </div>
    );
};
export default EZSCreditAuthentication;
